import React, { useEffect, useRef, useState } from "react";
import { CaretLeft, Check, SpeakerHigh, CircleNotch, CloudCheck, ShieldStar } from "@phosphor-icons/react";
import {
  TTS_VOICE_OPTIONS,
  TTS_PLAYBACK_OPTIONS,
  getTtsVoice,
  setTtsVoice,
  getTtsPlaybackMode,
  setTtsPlaybackMode,
} from "../../lib/ttsVoice";
import { clearCachedAudio } from "../../lib/phrasebook";
import { useAuth } from "../../lib/auth";
import api from "../../lib/api";
import { toast } from "sonner";

const PREVIEW_TEXT = "This is your TipLingo translation voice.";
const SHOWCASE_KEY = "tiplingo_voice_showcase_seen_v1";
// Two short lines per voice keep the full tour under ~28 seconds at
// OpenAI's ~1.5 words/sec rate (8 + 7 = 15 words per voice × ~1s/word
// × 2 voices ≈ 24-28s including the brief gap between samples).
const SHOWCASE_LINES = [
  "Hi! I'm your TipLingo travel translator.",
  "Pick the voice that sounds best to you.",
];

function hasSeenShowcase() {
  try { return localStorage.getItem(SHOWCASE_KEY) === "1"; } catch { return false; }
}
function markShowcaseSeen() {
  try { localStorage.setItem(SHOWCASE_KEY, "1"); } catch { /* no-op */ }
}

/**
 * Settings → "Voice" sub-page. Two locks:
 *   1. Gender (Female / Male) — picks the OpenAI voice (nova / onyx) AND
 *      the matching device-native voice when fast mode is on.
 *   2. Playback quality (Premium / Fast) — Premium ALWAYS uses OpenAI so
 *      the user never accidentally hears an iOS "compact" voice that
 *      sounds creepy. Fast plays the device's native voice instantly
 *      with OpenAI as the fallback upgrade.
 *
 * Premium is the default precisely BECAUSE one user reported the native
 * picker grabbed a lo-fi "dying old man" voice for English Male
 * playback. Premium completely eliminates that risk.
 */
export default function VoiceGenderPicker({ onBack }) {
  const [value, setValue] = useState(getTtsVoice());
  const [mode, setMode] = useState(getTtsPlaybackMode());
  const [previewing, setPreviewing] = useState(null);
  // Tour state: null | "ready" | "female" | "male" | "done".
  // "ready" = banner visible, awaiting tap (iOS requires user gesture
  // before any audio plays). The other states mirror what's currently
  // speaking so we can highlight the active card.
  const [tour, setTour] = useState(() => (hasSeenShowcase() ? "done" : "ready"));
  const audioRef = useRef(null);
  const tourAbortRef = useRef(false);
  const { user } = useAuth();

  useEffect(() => {
    return () => {
      tourAbortRef.current = true;
      try {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
        }
      } catch { /* element disposed — ignore */ }
    };
  }, []);

  function getAudio() {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.preload = "auto";
    }
    return audioRef.current;
  }

  // Play a single TTS sample and resolve when playback ends, fails, or
  // a max-wait timeout fires. The timeout exists so a hung Audio
  // element (e.g. corrupted mp3 data, broken codec on an exotic
  // device) can't stall the tour indefinitely — worst case we move on
  // to the next line after 20s, which is well past any normal sample.
  async function playSample(openaiVoice, text) {
    const r = await api.post(`/tts`, { text, voice: openaiVoice });
    const b64 = r?.data?.audio_base64;
    if (!b64) throw new Error("no_audio");
    const audio = getAudio();
    audio.src = `data:audio/mp3;base64,${b64}`;
    audio.volume = 1;
    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        audio.removeEventListener("ended", done);
        audio.removeEventListener("error", done);
        clearTimeout(safety);
        resolve();
      };
      const safety = setTimeout(done, 20000);
      audio.addEventListener("ended", done);
      audio.addEventListener("error", done);
      audio.play().catch(() => {
        // iOS sometimes rejects first play after an awaited fetch.
        setTimeout(() => audio.play().catch(done), 60);
      });
    });
  }

  async function startTour() {
    tourAbortRef.current = false;
    try {
      setTour("female");
      for (const line of SHOWCASE_LINES) {
        if (tourAbortRef.current) return;
        await playSample("nova", line);
      }
      if (tourAbortRef.current) return;
      setTour("male");
      for (const line of SHOWCASE_LINES) {
        if (tourAbortRef.current) return;
        await playSample("onyx", line);
      }
    } catch {
      toast.error("Couldn't play the voice tour. Check your connection.");
    } finally {
      if (!tourAbortRef.current) {
        setTour("done");
        markShowcaseSeen();
      }
    }
  }

  function skipTour() {
    tourAbortRef.current = true;
    try {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    } catch { /* element disposed — ignore */ }
    setTour("done");
    markShowcaseSeen();
  }

  function choose(v) {
    setValue(v);
    setTtsVoice(v);
    clearCachedAudio();
  }

  function chooseMode(v) {
    setMode(v);
    setTtsPlaybackMode(v);
  }

  async function preview(opt, e) {
    if (e) e.stopPropagation();
    if (previewing) return;
    setPreviewing(opt.value);
    try {
      await playSample(opt.openaiVoice, PREVIEW_TEXT);
    } catch {
      toast.error("Couldn't preview voice. Check your connection and try again.");
    } finally {
      setPreviewing(null);
    }
  }

  return (
    <div
      className="min-h-screen bg-bone pb-24"
      data-testid="voice-gender-page"
    >
      <div className="sticky top-0 z-10 bg-bone/95 backdrop-blur-sm border-b border-navy/10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={onBack}
            data-testid="voice-gender-back-btn"
            className="w-9 h-9 rounded-full bg-white border border-navy/10 flex items-center justify-center"
          >
            <CaretLeft size={18} weight="bold" color="#0E2C54" />
          </button>
          <h1 className="font-jakarta font-extrabold text-navy text-xl">
            Voice
          </h1>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-4 space-y-5">
        <p className="font-jakarta text-sm text-navy-soft leading-relaxed">
          Tap{" "}
          <span className="font-bold text-navy">Preview</span> to hear each
          voice before saving. Your choice applies to every translation in
          the app.
        </p>

        {tour !== "done" && (
          <div
            data-testid="voice-showcase-card"
            className="rounded-2xl bg-gradient-to-br from-navy to-navy/85 text-white shadow-soft overflow-hidden"
          >
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-orange flex items-center justify-center shrink-0 mt-0.5">
                  <SpeakerHigh size={20} weight="duotone" color="#fff" />
                </div>
                <div className="flex-1">
                  <div className="font-jakarta font-extrabold text-base leading-tight">
                    Hear both voices in 30 seconds
                  </div>
                  <div className="font-jakarta text-[13px] text-white/80 mt-1 leading-snug">
                    Sample Female and Male back-to-back, then keep the one
                    you like for every translation.
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {tour === "ready" && (
                  <>
                    <button
                      type="button"
                      onClick={startTour}
                      data-testid="voice-showcase-play-btn"
                      className="flex-1 h-10 rounded-full bg-orange text-white font-jakarta font-extrabold text-sm flex items-center justify-center gap-1.5 active:opacity-80 transition-opacity"
                    >
                      <SpeakerHigh size={16} weight="fill" />
                      Play tour
                    </button>
                    <button
                      type="button"
                      onClick={skipTour}
                      data-testid="voice-showcase-skip-btn"
                      className="h-10 px-4 rounded-full bg-white/10 border border-white/20 text-white font-jakarta font-bold text-sm active:opacity-80 transition-opacity"
                    >
                      Skip
                    </button>
                  </>
                )}
                {(tour === "female" || tour === "male") && (
                  <>
                    <div
                      data-testid="voice-showcase-status"
                      className="flex-1 h-10 rounded-full bg-white/10 border border-white/20 text-white font-jakarta font-bold text-sm flex items-center justify-center gap-2"
                    >
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Now playing: {tour === "female" ? "Female (Nova)" : "Male (Onyx)"}
                    </div>
                    <button
                      type="button"
                      onClick={skipTour}
                      data-testid="voice-showcase-stop-btn"
                      className="h-10 px-4 rounded-full bg-white/10 border border-white/20 text-white font-jakarta font-bold text-sm active:opacity-80"
                    >
                      Stop
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {tour === "done" && (
          <button
            type="button"
            onClick={() => setTour("ready")}
            data-testid="voice-showcase-replay-btn"
            className="self-start font-jakarta font-bold text-[12px] text-orange underline-offset-4 hover:underline"
          >
            Replay 30-second voice tour
          </button>
        )}

        {user?.user_id && (
          <div
            data-testid="voice-sync-pill"
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-forest/10 border border-forest/20"
          >
            <CloudCheck size={16} weight="duotone" color="#2E7D5A" />
            <span className="font-jakarta text-[12px] text-forest font-bold leading-snug">
              Syncs to {user.email || "your account"} — sign in on another
              device to keep your voice.
            </span>
          </div>
        )}

        <section>
          <div className="font-jakarta font-extrabold text-navy text-[11px] uppercase tracking-widest mb-2">
            Voice
          </div>
          <div className="bg-white rounded-2xl shadow-soft border border-navy/5 divide-y divide-navy/5">
            {TTS_VOICE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className="w-full flex items-center justify-between p-4 active:bg-bone transition-colors"
              >
                <button
                  type="button"
                  onClick={() => choose(opt.value)}
                  data-testid={`voice-gender-option-${opt.value}`}
                  className="flex-1 pr-3 text-left"
                >
                  <div className="font-jakarta font-extrabold text-navy text-base">
                    {opt.label}
                  </div>
                  <div className="font-jakarta text-navy-soft text-[13px] mt-0.5">
                    {opt.description}
                  </div>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={(e) => preview(opt, e)}
                    disabled={previewing !== null}
                    data-testid={`voice-gender-preview-${opt.value}`}
                    aria-label={`Preview ${opt.label} voice`}
                    className="h-9 px-3 rounded-full bg-navy/5 border border-navy/10 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {previewing === opt.value ? (
                      <CircleNotch size={14} weight="bold" color="#0E2C54" className="animate-spin" />
                    ) : (
                      <SpeakerHigh size={14} weight="duotone" color="#0E2C54" />
                    )}
                    <span className="font-jakarta font-bold text-navy text-xs">
                      Preview
                    </span>
                  </button>
                  {value === opt.value && (
                    <div
                      className="w-7 h-7 rounded-full bg-orange flex items-center justify-center"
                      aria-label="Selected"
                    >
                      <Check size={16} weight="bold" color="#fff" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <div className="font-jakarta font-extrabold text-navy text-[11px] uppercase tracking-widest mb-2 flex items-center gap-1.5">
            <ShieldStar size={13} weight="duotone" color="#0E2C54" />
            Voice quality lock
          </div>
          <div className="bg-white rounded-2xl shadow-soft border border-navy/5 divide-y divide-navy/5">
            {TTS_PLAYBACK_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => chooseMode(opt.value)}
                data-testid={`voice-playback-${opt.value}`}
                className="w-full flex items-center justify-between p-4 text-left active:bg-bone transition-colors"
              >
                <div className="flex-1 pr-3">
                  <div className="font-jakarta font-extrabold text-navy text-base flex items-center gap-1.5">
                    {opt.label}
                    {opt.value === "premium" && (
                      <span className="font-jakarta font-bold text-[10px] text-forest uppercase tracking-wide bg-forest/10 px-1.5 py-0.5 rounded">
                        Recommended
                      </span>
                    )}
                  </div>
                  <div className="font-jakarta text-navy-soft text-[13px] mt-0.5">
                    {opt.description}
                  </div>
                </div>
                {mode === opt.value && (
                  <div
                    className="w-7 h-7 rounded-full bg-orange flex items-center justify-center shrink-0"
                    aria-label="Selected"
                  >
                    <Check size={16} weight="bold" color="#fff" />
                  </div>
                )}
              </button>
            ))}
          </div>
          <p className="font-jakarta text-[12px] text-navy-soft leading-relaxed pt-2">
            <span className="font-bold text-navy">Premium</span> guarantees a
            studio-quality voice that sounds identical on every device — no
            risk of an old-sounding iPhone system voice. Choose{" "}
            <span className="font-bold text-navy">Fast</span> only if you
            travel without reliable mobile data and prefer instant on-device
            playback.
          </p>
        </section>
      </div>
    </div>
  );
}
